/**
 * Identity-hygiene — Zod schemas.
 *
 * Mirrors the wire shapes served by
 * /api/v1/tenants/{id}/identity/hygiene/* in
 * internal/identity/hygiene. Single source of truth for the
 * frontend; every fetch returns a parsed shape so a wire-format
 * drift surfaces at the network boundary, not deep in a render.
 */

import { z } from "zod";

export const stalenessToneSchema = z.enum([
  "fresh",
  "watch",
  "stale",
  "critical",
]);
export type StalenessTone = z.infer<typeof stalenessToneSchema>;

export const grantSourceSchema = z.enum(["direct", "group", "both"]);
export type GrantSource = z.infer<typeof grantSourceSchema>;

export const userStatusSchema = z.enum([
  "active",
  "inactive",
  "disabled",
  "locked",
  "deleted",
]);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const dormantEntrySchema = z.object({
  tenant_id: z.string().uuid(),
  user_id: z.string().uuid(),
  user_email: z.string(),
  user_display_name: z.string(),
  user_status: z.string(),
  application_id: z.string().uuid(),
  application_name: z.string(),
  application_logo: z.string().optional().default(""),
  grant_source: grantSourceSchema,
  group_id: z.string().uuid().optional().nullable(),
  group_name: z.string().optional().default(""),
  // How many groups actually carry this entitlement. group_name is only
  // ONE of them (the representative), so rendering it alone implies
  // "revoke this group and the access is gone", which is false when this
  // is > 1. Optional for forward-compat with a backend that predates it.
  group_count: z.number().int().min(0).optional().default(0),
  granted_at: z.string(),
  last_used_at: z.string().optional().nullable(),
  days_idle: z.number().int().min(0),
  use_count: z.number().int().min(0),
  severity: stalenessToneSchema,
});
export type DormantEntry = z.infer<typeof dormantEntrySchema>;

export const hygieneSummarySchema = z.object({
  total_entitlements: z.number().int().min(0),
  fresh_count: z.number().int().min(0),
  watch_count: z.number().int().min(0),
  stale_count: z.number().int().min(0),
  critical_count: z.number().int().min(0),
  never_used_count: z.number().int().min(0),
  unique_users: z.number().int().min(0),
  unique_applications: z.number().int().min(0),
  hygiene_score: z.number().int().min(0).max(100),
  oldest_refreshed_at: z.string(),
  generated_at: z.string(),
});
export type HygieneSummary = z.infer<typeof hygieneSummarySchema>;

export const dormantListSchema = z.object({
  data: z.array(dormantEntrySchema),
  pagination: z.object({
    total: z.number().int().min(0),
    offset: z.number().int().min(0),
    limit: z.number().int().min(1),
  }),
  summary: hygieneSummarySchema,
});
export type DormantList = z.infer<typeof dormantListSchema>;

// ── Auto-revoke policy ────────────────────────────────────────────────────

export const policySchema = z.object({
  tenant_id: z.string().uuid().optional(),
  auto_revoke_enabled: z.boolean(),
  threshold_days: z.number().int().min(14).max(730),
  warn_days_before: z.number().int().min(0).max(30),
  max_revokes_per_tick: z.number().int().min(1).max(1000),
  requires_co_approval: z.boolean(),
  updated_at: z.string().optional(),
  updated_by: z.string().uuid().nullable().optional(),
  co_approved_by: z.string().uuid().nullable().optional(),
  co_approved_at: z.string().nullable().optional(),
});
export type Policy = z.infer<typeof policySchema>;

// ── Revoke log ─────────────────────────────────────────────────────────────

export const revokeLogEntrySchema = z.object({
  id: z.string().uuid(),
  user_id: z.string().uuid(),
  user_email: z.string(),
  application_id: z.string().uuid(),
  application_name: z.string(),
  grant_source: z.string(),
  group_name: z.string().optional().default(""),
  threshold_days: z.number().int(),
  days_idle_at_revoke: z.number().int(),
  warned_at: z.string().nullable().optional(),
  revoked_at: z.string(),
  re_granted_at: z.string().nullable().optional(),
});
export type RevokeLogEntry = z.infer<typeof revokeLogEntrySchema>;

export const revokeLogListSchema = z.object({
  data: z.array(revokeLogEntrySchema),
});
export type RevokeLogList = z.infer<typeof revokeLogListSchema>;

// ── Certification campaigns ──────────────────────────────────────────────

export const campaignStatusSchema = z.enum(["draft", "open", "closed", "cancelled"]);
export type CampaignStatus = z.infer<typeof campaignStatusSchema>;

export const itemDecisionSchema = z.enum([
  "pending",
  "approved",
  "revoked",
  "unknown",
  "needs_clarification",
  "resolved_external",
]);
export type ItemDecision = z.infer<typeof itemDecisionSchema>;

export const campaignScopeSchema = z.object({
  min_days_idle: z.number().int().min(0).optional(),
  tone: z.string().optional(),
  application_ids: z.array(z.string().uuid()).optional(),
  user_ids: z.array(z.string().uuid()).optional(),
});
export type CampaignScope = z.infer<typeof campaignScopeSchema>;

export const campaignSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  name: z.string(),
  description: z.string().optional().default(""),
  status: campaignStatusSchema,
  opens_at: z.string(),
  closes_at: z.string(),
  scope: campaignScopeSchema.optional().default({}),
  created_at: z.string(),
  created_by: z.string().uuid().nullable().optional(),
  closed_at: z.string().nullable().optional(),
  closed_by: z.string().uuid().nullable().optional(),
  total_items: z.number().int().min(0),
  pending_items: z.number().int().min(0),
  approved_items: z.number().int().min(0),
  revoked_items: z.number().int().min(0),
});
export type Campaign = z.infer<typeof campaignSchema>;

export const campaignListSchema = z.object({
  data: z.array(campaignSchema),
});

export { campaignItemSchema, campaignItemListSchema } from "@/lib/certification-contract";
export type CampaignItem = z.infer<typeof import("@/lib/certification-contract").campaignItemSchema>;

export const dormantFilterSearchSchema = z.object({
  section: z.enum(["dormant", "campaigns"]).optional(),
  tone: stalenessToneSchema.optional(),
  grant_source: grantSourceSchema.optional(),
  application_id: z.string().uuid().optional(),
  user_id: z.string().uuid().optional(),
  min_days_idle: z.coerce.number().int().min(0).max(365).optional(),
  include_fresh: z.coerce.boolean().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
export type DormantFilterSearch = z.infer<typeof dormantFilterSearchSchema>;
